import { defineStore } from "pinia";
import SettingResource from "~/models/course-planner/Setting";
import StudentResource from "~/models/course-planner/Student";
import CourseRequestResource from "~/models/course-planner/CourseRequest";
import CourseResource from "~/models/course-planner/Course";
import DepartmentResource from "~/models/course-planner/Department";

export const useCoursePlannerStore = defineStore("courseplanner", {
  state: () => ({
    settings_schema: [
      {
        field_type: "QInput",
        name: "school_year",
        empty_value: "",
        setup: {
          label: "School Year",
          type: "number",
        },
      },
      {
        field_type: "QInput",
        name: "max_credits",
        empty_value: "",
        setup: {
          label: "Max Credits",
          type: "number",
          step: 0.25,
        },
      },
      {
        field_type: "QInput",
        name: "core_depts",
        empty_value: "",
        setup: {
          label: "Core Departments (csv)",
        },
      },
      {
        field_type: "QInput",
        name: "core_exclude_courses",
        empty_value: "",
        setup: {
          label: "Exclude Core Department Courses (csv)",
        },
      },
      {
        field_type: "QInput",
        name: "noncore_depts_with_core_reqs",
        empty_value: "",
        setup: {
          label: "Non-Core Departments with Core Reqs (csv)",
        },
      },
      {
        field_type: "QSelect",
        name: "system_status",
        empty_value: "",
        setup: {
          label: "System Status",
        },
      },
      {
        field_type: "QInput",
        name: "high_request_courses",
        empty_value: "",
        setup: {
          label: "High Request Courses (csv)",
        },
      },
      {
        field_type: "QInput",
        name: "gateway_courses",
        empty_value: "",
        setup: {
          label: "Gateway Courses (json)",
        },
      },
      {
        field_type: "QInput",
        name: "caseload_overrides",
        empty_value: "",
        setup: {
          label: "Caseload Overrides (json)",
        },
      },
      {
        field_type: "QInput",
        name: "request_change_end_date",
        empty_value: "",
        setup: {
          label: "Last Day Students Can Request Changes (yyyy-mm-dd)",
        },
      },
    ],

    // System Config
    system_status: null,
    school_year: null,
    max_credits: null,
    core_depts: [],
    core_exclude_courses: [],
    noncore_depts_with_core_reqs: [],
    departments: [],
    graduation_requirement_types: [],
    high_request_courses: [],
    caseload_overrides: {},
    course_catalog: [],

    // Student Config
    loading_student: false,
    student_id: null,
    student: null,
    student_planner: [],
    student_planner_by_dept: [],

    // Student Course Cart
    student_cart_id: null,
    student_cart: { core: [], electives: [], alternates: [] },

    // Static System Config
    dept_grad_req_map: {
      ENG: "EN",
      MAT: "MA",
      PHY: "PE",
      SCI: "SC",
      SOC: "SS",
      FA: "FA",
      EL: "EL",
    },
    request_type_options: [
      { label: "Regular", value: "regular" },
      { label: "Alternate", value: "alternate" },
    ],
    mp_options: [
      { type: "Semester", label: "S1", value: "S1", disable: false },
      { type: "Semester", label: "S2", value: "S2", disable: false },
      { type: "Quarter", label: "Q1", value: "Q1", disable: true },
      { type: "Quarter", label: "Q2", value: "Q2", disable: true },
      { type: "Quarter", label: "Q3", value: "Q3", disable: true },
      { type: "Quarter", label: "Q4", value: "Q4", disable: true },
    ],
  }),
  getters: {
    totalStudentCartRequests: (state) =>
      state.student_cart.core.length +
      state.student_cart.electives.length +
      state.student_cart.alternates.length,
    isStudentCartReady: (state) =>
      !!state.student_cart_id &&
      !!state.student_id &&
      state.student_cart_id.trim() == state.student_id,
    userCanImpersonate: () => {
      const auth = useAuthStore();
      return auth.isAuthenticated &&
        auth.user.hasPermissionIn([
          "courseplanner-impersonate",
          "courseplanner-admin",
        ]);
    },
    userCanManage: (state) => {
      const auth = useAuthStore();
      return (
        auth.isAuthenticated &&
        auth.user.hasPermission("courseplanner-manage-requests") &&
        state.system_status != "closed"
      );
    },
    userCanMakeChanges: (state) => {
      const auth = useAuthStore();
      if (!auth.isAuthenticated) {
        return false;
      }
      if (auth.user.hasPermission("courseplanner-admin")) {
        return true;
      }
      if (auth.user.hasPermission("courseplanner-manage-requests")) {
        return state.system_status != "closed";
      }
      if (auth.user.sis_id == state.student_id) {
        return (
          state.system_status == "open" &&
          !state.student.current_planner_status.finalized_at
        );
      }
      return false;
    },
    userCaseload: (state) => {
      const auth = useAuthStore();

      if (!auth.user.hasPermission("courseplanner-manage-requests")) {
        return [];
      }

      if (
        Object.hasOwn(state.caseload_overrides, auth.user.username.toLowerCase())
      ) {
        return [
          ...state.caseload_overrides[auth.user.username.toLowerCase()],
          auth.user.username,
        ];
      }

      return [auth.user.username];
    },
    getNextAltRequestNumber: (state) => {
      if (!state.student || !state.student.current_staff_course_requests) {
        return 1;
      }
      return (
        state.student.current_staff_course_requests.filter(
          (req) => req.request_type == "alternate"
        ).length + 1
      );
    },
  },
  actions: {
    // Base System Info
    async loadConfig() {
      try {
        const response = await SettingResource.custom(
          "settings/metadata"
        ).first();

        this.system_status = response.system_status;
        this.school_year = response.school_year;
        this.max_credits = parseFloat(response.max_credits);
        this.core_depts = response.core_depts;
        this.core_exclude_courses = response.core_exclude_courses;
        this.caseload_overrides = response.caseload_overrides;
        this.noncore_depts_with_core_reqs =
          response.noncore_depts_with_core_reqs;
        this.departments = Object.values(response.departments).map(
          (d) => new DepartmentResource(d)
        );
        this.graduation_requirement_types =
          response.graduation_requirement_types;
        this.high_request_courses = response.high_request_courses;

        this.course_catalog = response.course_catalog.map(
          (c) => new CourseResource(c)
        );

        return response;
      } catch (error) {
        console.log("ERROR Loading CoursePlanner Config");
        const { $bugsnag } = useNuxtApp();
        $bugsnag.notify(error);
        if (!e.response) {
          console.log(error);
        } else {
          console.log(error.response.data.message);
        }
      }
    },

    // Base Student Info
    setStudent(payload) {
      console.log("setStudent: ", payload.STUDENT_ID);

      this.student_id = parseInt(payload.STUDENT_ID.trim());
      this.student = new StudentResource(payload);
      if (this.student_cart_id && this.student_cart_id != this.student_id) {
        this.resetCourseCart();
      }
    },
    resetStudent() {
      console.log("resetStudent");

      this.student_id = null;
      this.student = null;
      this.student_planner = [];

      this.resetCourseCart();
    },
    setPlanner(planner) {
      this.student_planner = planner.map((c) => new CourseResource(c));

      // Organize Planner Courses By Dept
      this.student_planner_by_dept = {};
      if (!this.student_planner || this.student_planner.length == 0) {
        return;
      }

      this.student_planner.forEach((c) => {
        if (!c.DEPARTMENT) {
          c.DEPARTMENT = "MISC";
        }

        if (Object.hasOwn(this.student_planner_by_dept, c.DEPARTMENT.trim())) {
          this.student_planner_by_dept[c.DEPARTMENT.trim()].courses.push(c);

          if (c.core_grad_requirement) {
            this.student_planner_by_dept[c.DEPARTMENT.trim()].core_courses.push(
              c
            );
          }
          return;
        }

        Object.defineProperty(
          this.student_planner_by_dept,
          c.DEPARTMENT.trim(),
          {
            value: c.department_info
              ? c.department_info
              : {
                CODE: c.DEPARTMENT,
                ACTIVE: "Y",
                DESCRIPTION: `${c.DEPARTMENT.trim()} Department`,
              },
            enumerable: true,
            writable: true,
          }
        );

        Object.defineProperty(
          this.student_planner_by_dept[c.DEPARTMENT.trim()],
          "courses",
          {
            value: [],
            enumerable: true,
            writable: true,
          }
        );

        Object.defineProperty(
          this.student_planner_by_dept[c.DEPARTMENT.trim()],
          "core_courses",
          {
            value: [],
            enumerable: true,
            writable: true,
          }
        );

        this.student_planner_by_dept[c.DEPARTMENT.trim()].courses.push(c);

        if (c.core_grad_requirement) {
          this.student_planner_by_dept[c.DEPARTMENT.trim()].core_courses.push(
            c
          );
        }
      });
    },

    async loadCoursePlannerStudent(studentId) {
      this.loading_student = true;

      try {
        const auth = useAuthStore();

        if (!studentId) {
          studentId = this.student_id ? this.student_id : auth.user.sis_id;
        }

        if (!studentId) {
          this.resetStudent();
          return;
        }

        if (studentId != this.student_id) {
          this.resetStudent();
        }

        const student_response = await StudentResource.find(studentId);
        this.setStudent(student_response.data);
        if (student_response.planner) {
          this.setPlanner(student_response.planner);
        }
      } catch (error) {
        console.log(error);
      }

      this.loading_student = false;
    },

    // CoursePlanner Helpers : Cart
    setupCourseCart() {
      this.student_cart_id = this.student_id;
      this.student_cart = { core: [], electives: [], alternates: [] };
    },

    updateCourseCart(requestType, requests) {
      if (!this.student_cart_id) {
        this.setupCourseCart();
      }
      this.student_cart[requestType] = [];
      for (var i in requests) {
        if (requests[i].is_finalized || !requests[i].course) {
          continue;
        }
        this.student_cart[requestType].push(requests[i]);
      }
    },

    resetCourseCart() {
      this.student_cart_id = null;
      this.student_cart = { core: [], electives: [], alternates: [] };
    },

    getPlannerCourse(courseId) {
      if (!courseId || !this.course_catalog) {
        return null;
      }
      return (
        this.course_catalog.find((course) => {
          return courseId.trim() == course.COURSE.trim();
        }) || null
      );
    },

    async saveStudentCart() {
      const error = {
        response: {
          data: {
            message: null,
            errors: [],
          },
        },
      };
      if (!this.student_cart_id) {
        error.response.data.message =
          "Cart was never initialized for a student.";
        throw error;
      }

      if (this.totalStudentCartRequests <= 0) {
        error.response.data.message = "No items in cart to save.";
        throw error;
      }

      try {
        console.log("Saving Course Cart");

        const form = new useForm(
          {
            core: this.student_cart.core.map(this.requestToFormReq),
            electives: this.student_cart.electives.map(this.requestToFormReq),
            alternates: this.student_cart.alternates.map(this.requestToFormReq),
          },
          {
            useResource: new CourseRequestResource().for(this.student),
            resetOnSuccess: false,
          }
        );

        const cart_response = await form.save();
        this.resetCourseCart();

        if (cart_response.student) {
          this.setStudent(cart_response.student);
        }
        return cart_response;
      } catch (error) {
        console.log("Error Saving Student Cart", error);
        if (error.response) {
          console.log(
            "Error Response data",
            JSON.stringify(error.response.data)
          );
        }
        const { $bugsnag } = useNuxtApp();
        $bugsnag.notify(error);
        throw error;
      }
    },

    requestToFormReq(request) {
      const {
        alt_course,
        alt_course_priority,
        course,
        course_building,
        course_dept,
        is_finalized,
        request_type,
        requester_type,
        school_year,
        student_id,
      } = request;
      return {
        alt_course,
        alt_course_priority,
        course,
        course_building,
        course_dept,
        is_finalized,
        request_type,
        requester_type,
        school_year,
        student_id,
      };
    },

    getCourseCartRequestByDept(dept, requestType) {
      if (!this.student_cart[requestType].length) {
        return [];
      }
      dept = dept ? dept.trim() : dept;
      return (
        this.student_cart[requestType].filter(function (request) {
          var request_dept = request.course_dept
            ? request.course_dept.trim()
            : request.course_dept;
          return (
            request_dept == dept &&
            (!requestType || request.request_type == requestType)
          );
        }) || []
      );
    },

    isCourseInCart(course, requestType) {
      if (!this.student_cart[requestType].length || !course) {
        return false;
      }

      return (
        this.student_cart[requestType].filter(
          (req) => req.course && req.course.trim() == course.trim()
        ).length > 0
      );
    },

    isHighRequestCourse(courseId) {
      if (!courseId) {
        return false;
      }
      return (
        this.high_request_courses.findIndex(function (course) {
          return course.trim() == courseId.trim();
        }) !== -1
      );
    },

    getPlannerCoursesByDept(dept, core_only = false) {
      if (
        !this.student_planner ||
        (core_only && !this.dept_grad_req_map[dept])
      ) {
        return [];
      }
      dept = dept ? dept.trim() : dept;

      return (
        this.student_planner.filter((course) => {
          if (
            (course.DEPARTMENT
              ? course.DEPARTMENT.trim()
              : course.DEPARTMENT) != dept
          ) {
            return false;
          }
          if (
            this.core_exclude_courses.find((code) => {
              return code.trim() == course.COURSE.trim();
            })
          ) {
            return false;
          }

          if (core_only) {
            if (!course.graduation_requirements) {
              return false;
            }
            for (let i in course.graduation_requirements) {
              if (
                course.graduation_requirements[i].CODE.startsWith(
                  this.dept_grad_req_map[dept]
                )
              ) {
                return true;
              }
            }
            return false;
          }
          return true;
        }) || []
      );
    },
  },
});
